<template>
  <div>
    <b-form-group
      label="Référence colis"
      label-for="subcontractor-payment-scan"
    >
      <b-input-group class="input-group-merge">
        <b-input-group-prepend is-text>
          <feather-icon icon="MaximizeIcon" />
        </b-input-group-prepend>
        <b-form-input
          id="subcontractor-payment-scan"
          v-model="ref"
          placeholder="Scanner ou saisir une référence"
          @change="checkOrder"
        />
      </b-input-group>
    </b-form-group>

    <div class="payment-grid">
      <b-card>
        <b-form-group label="Société de sous-traitance">
          <v-select
            v-model="outsourcingCompany"
            :clearable="false"
            label="display_name"
            placeholder="Sélectionner une société"
            :options="outsourcingCompanies"
            @input="loadOrders"
          />
        </b-form-group>
        <b-form-group
          v-if="paymentCodeImportEnabled"
          label="Code payment"
          label-for="subcontractor-payment-code"
        >
          <b-input-group>
            <b-form-input
              id="subcontractor-payment-code"
              v-model="paymentCode"
              placeholder="Saisir le code payment"
              @keyup.enter="importPaymentDetails"
            />
            <b-input-group-append>
              <b-button
                variant="primary"
                :disabled="isImportingPayment || !paymentCode.trim()"
                @click="importPaymentDetails"
              >
                <b-spinner
                  v-if="isImportingPayment"
                  small
                />
                <feather-icon
                  v-else
                  icon="DownloadCloudIcon"
                />
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
        <b-form-group
          label="Importer un fichier Excel"
          label-for="subcontractor-payment-excel"
        >
          <b-form-file
            id="subcontractor-payment-excel"
            v-model="excelFile"
            accept=".xlsx,.xls"
            browse-text="Parcourir"
            placeholder="Sélectionner un fichier contenant external_id / id / Code Barres"
            drop-placeholder="Déposer le fichier ici"
            :disabled="!outsourcingCompany || isImportingExcel"
            @input="importExcelFile"
          />
          <small
            v-if="excelImportSummary"
            class="d-block text-muted mt-50"
          >
            {{ excelImportSummary }}
          </small>
        </b-form-group>
        <div class="font-weight-bold mb-1">
          Colis disponibles : {{ available.length }}
        </div>
        <vue-good-table
          :columns="columns"
          :rows="available"
          :search-options="{ enabled: true, placeholder: 'Rechercher' }"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <span v-if="props.column.field === 'action'">
              <feather-icon
                icon="CheckSquareIcon"
                class="cursor-pointer text-success"
                size="17"
                @click="addItem(props.row)"
              />
            </span>
            <span v-else-if="props.column.field === 'price_ttc'">
              {{ formatAmount(props.row.price_ttc) }} TND
            </span>
            <span v-else>{{ props.formattedRow[props.column.field] || '-' }}</span>
          </template>
        </vue-good-table>
      </b-card>

      <b-card>
        <div class="d-flex justify-content-between align-items-center flex-wrap mb-1">
          <div>
            <div class="font-weight-bold">
              Colis sélectionnés : {{ selected.length }}
            </div>
            <small class="text-muted">Total COD : {{ formatAmount(selectedTotal) }} TND</small>
            <br>
            <small class="text-muted">
              Total à recevoir : {{ formatAmountAbsolute(selectedAmountToReceive) }} TND
            </small>
          </div>
        </div>

        <vue-good-table
          :columns="columns"
          :rows="selected"
          :search-options="{ enabled: true, placeholder: 'Rechercher' }"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <span v-if="props.column.field === 'action'">
              <feather-icon
                icon="XCircleIcon"
                class="cursor-pointer text-danger"
                size="17"
                @click="removeItem(props.row)"
              />
            </span>
            <span v-else-if="props.column.field === 'price_ttc'">
              {{ formatAmount(props.row.price_ttc) }} TND
            </span>
            <span v-else>{{ props.formattedRow[props.column.field] || '-' }}</span>
          </template>
        </vue-good-table>

        <b-form-group
          label="Note"
          class="mt-2"
        >
          <b-form-textarea
            v-model="note"
            rows="2"
            placeholder="Note optionnelle"
          />
        </b-form-group>

        <div class="d-flex justify-content-end">
          <b-button
            variant="success"
            :disabled="isSaving || !outsourcingCompany || selected.length === 0"
            @click="save"
          >
            <b-spinner
              v-if="isSaving"
              small
              class="mr-1"
            />
            <feather-icon
              v-else
              icon="SaveIcon"
              class="mr-1"
            />
            Créer payment
          </b-button>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BFormFile,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BInputGroupAppend,
  BInputGroup,
  BInputGroupPrepend,
  BSpinner,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import { VueGoodTable } from 'vue-good-table'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { orderMatchesReference } from '@/utils/orderReference'
import {
  normalizeExternalId,
  parseExternalIdsFromArrayBuffer,
} from '@/utils/subcontractorPaymentExcel'

export default {
  name: 'PaymentSousTraitanceCreate',
  components: {
    BButton,
    BCard,
    BFormFile,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BInputGroup,
    BInputGroupAppend,
    BInputGroupPrepend,
    BSpinner,
    VueGoodTable,
    vSelect,
  },
  data() {
    return {
      ref: '',
      outsourcingCompany: null,
      available: [],
      selected: [],
      note: '',
      isSaving: false,
      isImportingPayment: false,
      paymentCode: '',
      excelFile: null,
      isImportingExcel: false,
      excelImportSummary: '',
      columns: [
        { label: 'Action', field: 'action', sortable: false },
        { label: 'Code', field: 'id' },
        { label: 'External ID', field: 'external_id' },
        { label: 'Nom', field: 'name' },
        { label: 'Téléphone', field: 'phone1' },
        { label: 'Gouvernorat', field: 'governorate' },
        { label: 'COD', field: 'price_ttc' },
      ],
    }
  },
  computed: {
    ...mapGetters('paymentSousTraitance', ['outsourcingCompanies', 'availableOrders']),
    selectedTotal() {
      return this.selected.reduce((total, order) => total + Number(order.price_ttc || 0), 0)
    },
    selectedDeliveryTotal() {
      return this.selected.length * Number(this.outsourcingCompany?.price_delivery || 0)
    },
    selectedAmountToReceive() {
      return this.selectedDeliveryTotal - this.selectedTotal
    },
    paymentCodeImportEnabled() {
      return Boolean(this.outsourcingCompany?.enable_subcontractor_payment_code_import)
    },
  },
  async created() {
    await this.fetchOutsourcingCompanies()
  },
  methods: {
    ...mapActions('paymentSousTraitance', [
      'fetchOutsourcingCompanies',
      'fetchAvailableOrders',
      'createPayment',
      'lookupPaymentDetails',
    ]),
    async loadOrders() {
      this.selected = []
      this.available = []
      this.paymentCode = ''
      this.excelFile = null
      this.excelImportSummary = ''
      if (!this.outsourcingCompany) return
      await this.fetchAvailableOrders({ outsourcing_company_id: this.outsourcingCompany.id })
      this.available = [...this.availableOrders]
    },
    readFileAsArrayBuffer(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = event => resolve(event.target.result)
        reader.onerror = () => reject(new Error('Impossible de lire le fichier Excel.'))
        reader.readAsArrayBuffer(file)
      })
    },
    async importExcelFile(file) {
      if (!file || !this.outsourcingCompany || this.isImportingExcel) return

      this.isImportingExcel = true
      this.excelImportSummary = ''
      try {
        const parsed = parseExternalIdsFromArrayBuffer(await this.readFileAsArrayBuffer(file))
        const availableByIdentifier = new Map()
        this.available.forEach(order => {
          const identifiers = [order.external_id, order.id]
          identifiers.forEach(value => {
            const identifier = normalizeExternalId(value)
            if (identifier && !availableByIdentifier.has(identifier)) {
              availableByIdentifier.set(identifier, order)
            }
          })
        })
        const selectedIdentifiers = new Set()
        this.selected.forEach(order => {
          const identifiers = [order.external_id, order.id]
          identifiers.forEach(value => {
            const identifier = normalizeExternalId(value)
            if (identifier) selectedIdentifiers.add(identifier)
          })
        })
        const matchedOrders = []
        let alreadySelectedCount = 0
        let missingCount = 0

        parsed.identifiers.forEach(identifier => {
          const order = availableByIdentifier.get(identifier)
          if (order) {
            matchedOrders.push(order)
          } else if (selectedIdentifiers.has(identifier)) {
            alreadySelectedCount += 1
          } else {
            missingCount += 1
          }
        })

        matchedOrders.forEach(order => this.addItem(order))
        this.excelImportSummary = `${parsed.rowCount} code(s) lu(s) dans « ${parsed.columnLabel} » : ${matchedOrders.length} ajouté(s), ${alreadySelectedCount} déjà sélectionné(s), ${missingCount} absent(s) des colis disponibles.`

        if (matchedOrders.length) {
          this.showToast(
            'success',
            `${matchedOrders.length} colis sélectionné(s) automatiquement depuis le fichier Excel.`,
          )
        } else {
          this.showToast('warning', 'Aucun colis du fichier n’est disponible pour cette société.')
        }
      } catch (err) {
        this.showToast('danger', err.message || 'Impossible de traiter le fichier Excel.')
      } finally {
        this.isImportingExcel = false
        this.excelFile = null
      }
    },
    async importPaymentDetails() {
      if (!this.outsourcingCompany || !this.paymentCode.trim() || this.isImportingPayment) return
      this.isImportingPayment = true
      try {
        const result = await this.lookupPaymentDetails({
          outsourcing_company_id: this.outsourcingCompany.id,
          payment_code: this.paymentCode.trim(),
        })
        const matchedOrders = result.matched_orders || []
        matchedOrders.forEach(order => this.addItem(order))
        const missingCount = (result.missing_identifiers || []).length
        const ignoredCount = result.ignored_partner_orders || 0
        if (matchedOrders.length) {
          this.showToast(
            'success',
            `${matchedOrders.length} colis ajouté(s). ${missingCount} non éligible(s), ${ignoredCount} ignoré(s).`,
          )
        } else {
          this.showToast('warning', 'Aucun colis éligible trouvé pour ce code payment.')
        }
      } catch (err) {
        this.showToast('danger', this.extractError(err))
      } finally {
        this.isImportingPayment = false
      }
    },
    checkOrder() {
      const searchRef = this.ref.trim()
      if (!searchRef) return
      const alreadySelected = this.selected.find(order => orderMatchesReference(order, searchRef))
      if (alreadySelected) {
        this.showToast('warning', 'Colis déjà sélectionné.')
        this.ref = ''
        return
      }
      const order = this.available.find(item => orderMatchesReference(item, searchRef))
      if (!order) {
        this.showToast('danger', 'Colis introuvable ou non éligible.')
        this.ref = ''
        return
      }
      this.addItem(order)
      this.ref = ''
    },
    addItem(order) {
      if (this.selected.find(item => item.id === order.id)) return
      this.selected.push(order)
      this.available = this.available.filter(item => item.id !== order.id)
    },
    removeItem(order) {
      this.selected = this.selected.filter(item => item.id !== order.id)
      this.available.push(order)
    },
    async save() {
      if (!this.outsourcingCompany || this.selected.length === 0) return
      this.isSaving = true
      try {
        const payment = await this.createPayment({
          outsourcing_company_id: this.outsourcingCompany.id,
          order_ids: this.selected.map(order => order.id),
          note: this.note,
        })
        this.showToast('success', 'Payment sous traitance créé.')
        this.$router.push({ name: 'payment-sous-traitance-detail', params: { id: payment.id } })
      } catch (err) {
        this.showToast('danger', this.extractError(err))
      } finally {
        this.isSaving = false
      }
    },
    extractError(err) {
      const data = err.response && err.response.data
      if (!data) return 'Une erreur est survenue.'
      if (data.detail) return data.detail
      if (data.non_field_errors) return data.non_field_errors.join(', ')
      const firstKey = Object.keys(data)[0]
      if (firstKey && Array.isArray(data[firstKey])) return data[firstKey].join(', ')
      if (firstKey) return String(data[firstKey])
      return 'Une erreur est survenue.'
    },
    showToast(variant, title) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          variant,
        },
      })
    },
    formatAmount(value) {
      return Number(value || 0).toFixed(3)
    },
    formatAmountAbsolute(value) {
      return Math.abs(Number(value || 0)).toFixed(3)
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style scoped>
.payment-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1rem;
}

@media (max-width: 991.98px) {
  .payment-grid {
    grid-template-columns: 1fr;
  }
}
</style>
